/**
 * useUnifiedNotificationCount
 *
 * Aggregates unread notification counts from the primary /notifications collection.
 * The primary collection is the only source the inbox displays, so the badge
 * intentionally matches what users see when they open the inbox.
 *
 * Legacy /users/{uid}/notifications subcollection is intentionally excluded —
 * it is written by the access-request flow but its documents are not shown in
 * the notification inbox and would inflate the badge count incorrectly.
 *
 * NOTE: The previous /cases/{caseId}/notifications subscription has been removed.
 * Case-scoped notifications are not displayed in the notification inbox UI, so
 * counting them inflated the badge. Additionally, subscribing to a sub-collection
 * of a case the user has no access to produced a Firestore permission error on
 * every Access Denied page.
 */

import { useEffect, useState } from 'react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '@/firebase';
import { useAuthState } from '@/auth/AuthProvider';

interface UseUnifiedNotificationCountOptions {
  activeCaseId?: string | null;
}

interface UseUnifiedNotificationCountReturn {
  totalUnread: number;
}

export function useUnifiedNotificationCount(
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _options: UseUnifiedNotificationCountOptions = {}
): UseUnifiedNotificationCountReturn {
  const { user } = useAuthState();
  const uid = user?.uid;

  const [totalUnread, setTotalUnread] = useState(0);

  // Subscribe: /notifications where recipientId == uid && status == 'unread'
  useEffect(() => {
    if (!uid) {
      setTotalUnread(0);
      return;
    }

    const q = query(
      collection(db, 'notifications'),
      where('recipientId', '==', uid),
      where('status', '==', 'unread')
    );

    const unsubscribe = onSnapshot(
      q,
      (snap) => setTotalUnread(snap.size),
      (err) => {
        console.error('[useUnifiedNotificationCount] subscription error:', err);
        setTotalUnread(0);
      }
    );

    return () => unsubscribe();
  }, [uid]);

  return { totalUnread };
}
